import { useState, useEffect, useCallback, useRef } from 'react'
import { StudentSummaryGenerationService, type GenerationScope, type BatchJob, type GenerationResult } from '../services/studentSummaryGeneration'
import { SchoolService, type School } from '../services/schools'
import { AdminSettingsService } from '../services/adminSettings'
import type { AdminSetting, AdminSettingKey, SettingsGroup } from '../types/adminSettings'
import { AI_MODELS } from '../constants/aiModels'
import { formatNumber } from '../utils/formatNumber'
import { normalizeClassName, normalizeGrade } from '../utils/gradeClass'
import { roleLabel, roleColor } from '../utils/roleLabel'

interface SummaryTargetResult {
  id: string
  name: string
  email: string
  role: string
  school_id: string | null
  grade: string | null
  class: string | null
}

const SummaryGenerationManager = () => {
  // Generation state
  const [scope, setScope] = useState<GenerationScope>('all')
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>('')
  const [selectedUser, setSelectedUser] = useState<SummaryTargetResult | null>(null)
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState<SummaryTargetResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  const [forceRegenerate, setForceRegenerate] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generationResult, setGenerationResult] = useState<GenerationResult | null>(null)

  // Schools data
  const [schools, setSchools] = useState<School[]>([])

  // Jobs state
  const [batchJobs, setBatchJobs] = useState<BatchJob[]>([])
  const [isLoadingJobs, setIsLoadingJobs] = useState(true)

  // Settings state
  const [settings, setSettings] = useState<AdminSetting[]>([])
  const [editedValues, setEditedValues] = useState<Record<string, string | number | boolean>>({})
  const [isLoadingSettings, setIsLoadingSettings] = useState(true)
  const [isSavingSettings, setIsSavingSettings] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [settingsSuccess, setSettingsSuccess] = useState<string | null>(null)

  // Auto-refresh for in-progress jobs
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const searchDropdownRef = useRef<HTMLDivElement>(null)

  // Settings groups for student summary
  const settingsGroups: SettingsGroup[] = [
    {
      category: 'student_summary',
      label: '生徒サマリー設定',
      fields: [
        {
          key: 'summary_lookback_days',
          label: '会話履歴対象日数',
          type: 'number',
          description: '生徒サマリー生成時に遡る日数 (1-365日)',
          min: 1,
          max: 365,
          step: 1
        },
        {
          key: 'summary_conversations_per_student',
          label: '分析する会話セッション数',
          type: 'number',
          description: '生徒ごとに分析する最新の会話セッション数 (1-10回)',
          min: 1,
          max: 10,
          step: 1
        },
        {
          key: 'summary_individual_prompt',
          label: '個別サマリー用プロンプト',
          type: 'textarea',
          description: '個別生徒サマリー生成時に使用するシステムプロンプト',
          rows: 15
        },
        {
          key: 'summary_aggregate_prompt',
          label: '集計レポート用プロンプト',
          type: 'textarea',
          description: 'クラス・学年・学校全体の集計レポート生成時に使用するシステムプロンプト',
          rows: 15
        },
        {
          key: 'summary_min_students_for_aggregate',
          label: '集計レポート最小生徒数',
          type: 'number',
          description: '集計レポートを生成するために必要な最小生徒数 (1-100人)',
          min: 1,
          max: 100,
          step: 1
        },
        {
          key: 'summary_individual_max_tokens',
          label: '個別サマリー最大トークン数',
          type: 'number',
          description: '個別生徒サマリーのAI応答最大トークン数 (100-4000)',
          min: 100,
          max: 4000,
          step: 100
        },
        {
          key: 'summary_aggregate_max_tokens',
          label: '集計レポート最大トークン数',
          type: 'number',
          description: '集計レポートのAI応答最大トークン数 (100-4000)',
          min: 100,
          max: 4000,
          step: 100
        },
        {
          key: 'summary_ai_model',
          label: 'サマリー生成AIモデル',
          type: 'select',
          description: '生徒サマリー・集計レポート生成で使用するAIモデル (個別・集計共通)',
          options: AI_MODELS
        },
        {
          key: 'summary_ai_temperature',
          label: 'サマリー生成温度パラメータ',
          type: 'number',
          description: '生徒サマリー・集計レポート生成時の温度 (0.0-2.0, 個別・集計共通)',
          min: 0,
          max: 2,
          step: 0.1
        }
      ]
    }
  ]

  // Load initial data
  useEffect(() => {
    loadSchools()
    loadBatchJobs()
    loadSettings()
  }, [])

  // Click outside handler for search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Auto-refresh logic
  useEffect(() => {
    const hasInProgressJobs = batchJobs.some(job =>
      ['pending', 'validating', 'in_progress', 'finalizing'].includes(job.status)
    )

    if (hasInProgressJobs && !refreshIntervalRef.current) {
      refreshIntervalRef.current = setInterval(() => {
        loadBatchJobs()
      }, 10000) // Refresh every 10 seconds
    } else if (!hasInProgressJobs && refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
      refreshIntervalRef.current = null
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [batchJobs])

  // User search with debounce
  const searchSummaryTargets = useCallback(async (query: string) => {
    if (query.length < 2) {
      setUserSearchResults([])
      return
    }

    setIsSearching(true)
    try {
      const results = await StudentSummaryGenerationService.searchSummaryTargets(query)
      setUserSearchResults(results)
      setShowSearchDropdown(true)
    } catch (error) {
      console.error('Error searching summary targets:', error)
    } finally {
      setIsSearching(false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchQuery) {
        searchSummaryTargets(userSearchQuery)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [userSearchQuery, searchSummaryTargets])

  const loadSchools = async () => {
    try {
      const schoolsData = await SchoolService.getSchools()
      setSchools(schoolsData)
    } catch (error) {
      console.error('Error loading schools:', error)
    }
  }

  const loadBatchJobs = async () => {
    setIsLoadingJobs(true)
    try {
      const jobs = await StudentSummaryGenerationService.getRecentBatchJobs(10)
      setBatchJobs(jobs)
    } catch (error) {
      console.error('Error loading batch jobs:', error)
    } finally {
      setIsLoadingJobs(false)
    }
  }

  const loadSettings = async () => {
    setIsLoadingSettings(true)
    try {
      const response = await AdminSettingsService.getAllSettings()
      if (response.success) {
        setSettings(response.data)
        const initialValues: Record<string, string | number | boolean> = {}
        response.data.forEach(setting => {
          initialValues[setting.key] = setting.value as string | number | boolean
        })
        setEditedValues(initialValues)
      }
    } catch (error) {
      console.error('Error loading settings:', error)
    } finally {
      setIsLoadingSettings(false)
    }
  }

  const handleGenerate = async () => {
    setIsGenerating(true)
    setGenerationResult(null)

    try {
      const result = await StudentSummaryGenerationService.triggerGeneration({
        scope,
        school_id: scope === 'school' ? selectedSchoolId : undefined,
        user_id: scope === 'student' ? selectedUser?.id : undefined,
        force: forceRegenerate
      })

      setGenerationResult(result)

      if (result.success) {
        // Refresh jobs list
        await loadBatchJobs()
      }
    } catch (error) {
      console.error('Error generating summaries:', error)
      setGenerationResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSettingChange = (key: AdminSettingKey, value: string | number | boolean) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleSaveSettings = async () => {
    setIsSavingSettings(true)
    setSettingsError(null)
    setSettingsSuccess(null)

    try {
      const updates = Object.entries(editedValues)
        .filter(([key]) => key.startsWith('summary_'))
        .map(([key, value]) => ({ key: key as AdminSettingKey, value }))
        .filter(update => {
          const currentSetting = settings.find(s => s.key === update.key)
          return currentSetting && JSON.stringify(currentSetting.value) !== JSON.stringify(update.value)
        })

      if (updates.length === 0) {
        setSettingsSuccess('変更はありません')
        return
      }

      const result = await AdminSettingsService.updateMultipleSettings(updates)

      if (result.success) {
        setSettingsSuccess(`${updates.length}件の設定を更新しました`)
        await loadSettings()
      } else {
        setSettingsError(`更新エラー: ${result.errors.join(', ')}`)
      }
    } catch (error) {
      setSettingsError('設定の保存中にエラーが発生しました')
      console.error('Error saving settings:', error)
    } finally {
      setIsSavingSettings(false)
    }
  }

  const handleResetSettings = () => {
    const resetValues: Record<string, string | number | boolean> = {}
    settings.forEach(setting => {
      resetValues[setting.key] = setting.value as string | number | boolean
    })
    setEditedValues(resetValues)
    setSettingsSuccess('変更をリセットしました')
  }

  const renderSettingField = (field: SettingsGroup['fields'][0]) => {
    const value = editedValues[field.key] ?? ''

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            value={String(value)}
            onChange={(e) => handleSettingChange(field.key, e.target.value)}
            rows={field.rows || 4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            placeholder={field.description}
          />
        )

      case 'number':
        return (
          <input
            type="number"
            value={Number(value)}
            onChange={(e) => handleSettingChange(field.key, parseFloat(e.target.value) || 0)}
            min={field.min}
            max={field.max}
            step={field.step}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        )

      case 'select':
        return (
          <select
            value={String(value)}
            onChange={(e) => handleSettingChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )

      default:
        return (
          <input
            type="text"
            value={String(value)}
            onChange={(e) => handleSettingChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={field.description}
          />
        )
    }
  }

  const getStatusBadge = (status: BatchJob['status']) => {
    const statusConfig: Record<BatchJob['status'], { color: string; label: string }> = {
      pending: { color: 'bg-gray-100 text-gray-800', label: '待機中' },
      validating: { color: 'bg-yellow-100 text-yellow-800', label: '検証中' },
      in_progress: { color: 'bg-blue-100 text-blue-800', label: '処理中' },
      finalizing: { color: 'bg-purple-100 text-purple-800', label: '完了処理中' },
      completed: { color: 'bg-green-100 text-green-800', label: '完了' },
      failed: { color: 'bg-red-100 text-red-800', label: '失敗' },
      expired: { color: 'bg-orange-100 text-orange-800', label: '期限切れ' },
      cancelled: { color: 'bg-gray-100 text-gray-800', label: 'キャンセル' }
    }

    const config = statusConfig[status]
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const canGenerate = () => {
    if (isGenerating) return false
    if (scope === 'school' && !selectedSchoolId) return false
    if (scope === 'student' && !selectedUser) return false
    return true
  }

  return (
    <div className="p-6 space-y-8">
      <h2 className="text-2xl font-bold text-gray-900">サマリー生成</h2>

      {/* Manual Generation Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">手動生成</h3>

        {/* Scope Selection */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">生成対象スコープ</label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="scope"
                  value="all"
                  checked={scope === 'all'}
                  onChange={() => setScope('all')}
                  className="mr-2"
                />
                <span>全対象ユーザー</span>
              </label>

              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="scope"
                    value="school"
                    checked={scope === 'school'}
                    onChange={() => setScope('school')}
                    className="mr-2"
                  />
                  <span>学校指定</span>
                </label>
                {scope === 'school' && (
                  <select
                    value={selectedSchoolId}
                    onChange={(e) => setSelectedSchoolId(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">学校を選択...</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>{school.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="scope"
                    value="student"
                    checked={scope === 'student'}
                    onChange={() => setScope('student')}
                    className="mr-2"
                  />
                  <span>ユーザー指定</span>
                </label>
                {scope === 'student' && (
                  <div className="relative flex-1 max-w-md" ref={searchDropdownRef}>
                    {selectedUser ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${roleColor(selectedUser.role)}`}>
                          {roleLabel(selectedUser.role)}
                        </span>
                        <span className="text-sm">
                          {selectedUser.name} ({selectedUser.email})
                        </span>
                        <button
                          onClick={() => {
                            setSelectedUser(null)
                            setUserSearchQuery('')
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                          onFocus={() => userSearchResults.length > 0 && setShowSearchDropdown(true)}
                          placeholder="名前、メール、または完全なIDで検索..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {isSearching && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          </div>
                        )}
                        {showSearchDropdown && userSearchResults.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                            {userSearchResults.map(user => (
                              <button
                                key={user.id}
                                onClick={() => {
                                  setSelectedUser(user)
                                  setShowSearchDropdown(false)
                                  setUserSearchQuery('')
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${roleColor(user.role)}`}>
                                    {roleLabel(user.role)}
                                  </span>
                                  <span className="font-medium text-sm">{user.name}</span>
                                </div>
                                <div className="text-xs text-gray-500">
                                  {user.email}
                                  {user.grade && user.class && ` | ${normalizeGrade(user.grade)}${normalizeClassName(user.class)}`}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Force Regenerate Option */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="force-regenerate"
              checked={forceRegenerate}
              onChange={(e) => setForceRegenerate(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="force-regenerate" className="ml-2 text-sm text-gray-700">
              強制再生成（既存サマリーがあっても生成する）
            </label>
          </div>

          {/* Generate Button */}
          <div className="pt-4">
            <button
              onClick={handleGenerate}
              disabled={!canGenerate()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isGenerating ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  生成中...
                </span>
              ) : (
                'サマリー生成を実行'
              )}
            </button>
          </div>

          {/* Generation Result */}
          {generationResult && (
            <div className={`mt-4 p-4 rounded-lg ${generationResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {generationResult.success ? (
                <div>
                  <p className="text-green-800 font-medium">
                    {generationResult.message || `サマリー生成ジョブを開始しました`}
                  </p>
                  {generationResult.student_count !== undefined && (
                    <p className="text-green-700 text-sm mt-1">
                      対象ユーザー数: {formatNumber(generationResult.student_count)}人
                    </p>
                  )}
                  {generationResult.filtering_stats && (
                    <p className="text-green-700 text-sm">
                      (活動あり: {formatNumber(generationResult.filtering_stats.total_students_with_activity)}人,
                      スキップ: {formatNumber(generationResult.filtering_stats.skipped_already_summarized + generationResult.filtering_stats.skipped_no_new_activity)}人)
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-red-800">
                  エラー: {generationResult.error}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Recent Jobs Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">最近のジョブ</h3>
          <button
            onClick={loadBatchJobs}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            更新
          </button>
        </div>

        {isLoadingJobs ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : batchJobs.length === 0 ? (
          <p className="text-center text-gray-500 py-8">ジョブがありません</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ステータス</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">対象人数</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">完了</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">失敗</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">キャリアサマリのみ</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">開始日時</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {batchJobs.map(job => (
                  <tr key={job.id}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getStatusBadge(job.status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {job.total_requests}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {job.completed_requests ?? 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {job.failed_requests ?? 0}
                    </td>
                    {/* '-' rather than 0 for rows written before this was recorded, so
                        "none this run" stays distinguishable from "not tracked yet". */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {job.metadata?.portfolio_only_count !== undefined
                        ? formatNumber(job.metadata.portfolio_only_count)
                        : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(job.submitted_at || job.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Settings Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">生徒サマリー設定</h3>
          <div className="flex gap-3">
            <button
              onClick={handleResetSettings}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              リセット
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={isSavingSettings}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSavingSettings ? '保存中...' : '保存'}
            </button>
          </div>
        </div>

        {settingsError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{settingsError}</p>
          </div>
        )}

        {settingsSuccess && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800">{settingsSuccess}</p>
          </div>
        )}

        {isLoadingSettings ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {settingsGroups.map(group => (
              <div key={group.category}>
                {group.fields.map(field => (
                  <div key={field.key} className="space-y-2 mb-6">
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label}
                    </label>
                    {renderSettingField(field)}
                    {field.description && (
                      <p className="text-sm text-gray-500">{field.description}</p>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default SummaryGenerationManager
